if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.onItemClick = this.onItemClick.bind(this);
        this.openSelector = this.openSelector.bind(this);
        this.closeSelector = this.closeSelector.bind(this);
        this.onContainerKeyUp = this.onContainerKeyUp.bind(this);
        this.onContainerKeyDown = this.onContainerKeyDown.bind(this);
        this.filterCountries = this.filterCountries.bind(this);
        this.hidePanel = this.hidePanel.bind(this);
        this.resetFilter = this.resetFilter.bind(this);
        this.onSearchFocus = this.onSearchFocus.bind(this);
        this.onSearchBlur = this.onSearchBlur.bind(this);
        this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
      }

      connectedCallback() {
        this.cacheElements();
        if (this.isBound) return;
        this.isBound = true;

        this.addEventListener('keyup', this.onContainerKeyUp);
        this.addEventListener('keydown', this.onContainerKeyDown);
        this.addEventListener('focusout', this.closeSelector);

        if (this.elements.button) {
          this.elements.button.addEventListener('click', this.openSelector);
        }

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries);
          this.elements.search.addEventListener('focus', this.onSearchFocus);
          this.elements.search.addEventListener('blur', this.onSearchBlur);
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown);
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel);
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter);
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a[data-value]').forEach((item) =>
          item.addEventListener('click', this.onItemClick)
        );

        // Never POST country forms through theme-dev proxy (502 Bad Gateway).
        this.querySelectorAll('form').forEach((form) => {
          if (form.querySelector('input[name="country_code"]')) {
            form.addEventListener('submit', this.onFormSubmit);
          }
        });
      }

      cacheElements() {
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('.country-filter__input, input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results]'),
        };
      }

      isCountrySelector() {
        return Boolean(this.querySelector('input[name="country_code"]'));
      }

      switchCountry(countryCode) {
        if (!countryCode) return;
        const url = new URL(window.location.href);
        url.searchParams.set('country', countryCode);
        url.searchParams.delete('currency');
        window.location.assign(`${url.pathname}${url.search}${url.hash}`);
      }

      onFormSubmit(event) {
        if (!this.isCountrySelector()) return;
        event.preventDefault();
        const input = this.querySelector('input[name="country_code"]');
        this.switchCountry(input?.value);
      }

      hidePanel() {
        if (!this.elements.button || !this.elements.panel) return;
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a[data-value]')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button?.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button?.focus();
            break;
          case 'SPACE':
            if (this.elements.button?.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        event.stopPropagation();
        const value = event.currentTarget.dataset.value;
        if (!value) return;

        if (this.elements.input) this.elements.input.value = value;
        if (this.elements.search) this.elements.search.disabled = true;

        // Country/region via Markets ?country= — avoids /localization 502 in theme preview.
        if (this.isCountrySelector()) {
          this.switchCountry(value);
          return;
        }

        // Language still uses the localization form POST.
        const form = this.querySelector('form');
        let returnTo = form?.querySelector('input[name="return_to"]');
        if (form && !returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          form.appendChild(returnTo);
        }
        if (returnTo) {
          const url = new URL(window.location.href);
          url.searchParams.delete('country');
          url.searchParams.delete('currency');
          returnTo.value = `${url.pathname}${url.search}${url.hash}` || '/';
        }
        if (form) form.submit();
      }

      openSelector() {
        this.cacheElements();
        if (!this.elements.button || !this.elements.panel) return;
        const inPopover =
          Boolean(this.closest('[data-auracase-preferences]')) ||
          Boolean(this.closest('.footer__locale')) ||
          Boolean(this.closest('.menu-drawer__preferences'));

        this.elements.button.focus({ preventScroll: true });
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );

        // Body overflow lock shifts the page when the scrollbar disappears — skip in popovers.
        if (!inPopover && !document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }

        if (this.elements.search && this.mql.matches && !inPopover) {
          this.elements.search.focus({ preventScroll: true });
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        if (!inPopover) {
          document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
        }
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a[data-value]');
        let visibleCountries = 0;

        this.elements.resetButton?.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', Boolean(searchValue));
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country')?.textContent || '');
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries += 1;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector')?.scrollTo?.(0, 0);
        this.querySelector('.country-selector__list')?.scrollTo?.(0, 0);
      }

      resetFilter(event) {
        event.stopPropagation();
        if (!this.elements.search) return;
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon?.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search?.value) {
          this.elements.searchIcon?.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
